import React, { useState, useRef } from 'react';
import { Col, Dropdown, Row, Table } from 'react-bootstrap';
import { GetJobLogs, GetJobLogs_getJobLogs as JobLogDetailsType } from '../../../types/schemaTypes';
import { useQuery } from '@apollo/client/react';
import { DateTimePicker, Error, Loading } from '../../_common';
import {
    GetJobLogSummary,
    GetJobLogSummary_getJobLogSummary_logTypesSummary as LogTypesSummaryType,
    GetJobLogSummary_getJobLogSummary_pivotData as PivotDataType,
} from '../../../types/schemaTypes';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    Text,
    LineChart,
    Legend,
    Line,
    Label,
} from 'recharts';
import { GET_JOB_LOGS_SUMMARY } from '../../../graphql/client/queries';

interface JobLogSummaryProps {
    jobId: number;
    setJobName: React.Dispatch<React.SetStateAction<string>>;
    toggle: 'count' | 'time';
}

const JobLogSummary = ({ jobId, setJobName, toggle }: JobLogSummaryProps): React.ReactElement => {
    const LegendColorsForPie = ['#145DA0', '#60A3D9', '#2EB5E0', '#155F7F'];
    const LegendColors = ['#787D12', '#4C899F', '#DBA40E', '#4B5A20', '#7A871E'];

    const today = new Date();
    const [toDate, setToDate] = useState(
        `${today.getFullYear()}-${today.getMonth() + 1 < 10 ? `0${today.getMonth() + 1}` : today.getMonth() + 1}-${
            today.getDate() < 10 ? `0${today.getDate()}` : today.getDate()
        }`,
    );

    const tempFromDate = new Date();
    if (window.screen.width > 1199) tempFromDate.setDate(today.getDate() - 13);
    else tempFromDate.setDate(today.getDate() - 6);

    const [fromDate, setFromDate] = useState(
        `${tempFromDate.getFullYear()}-${
            tempFromDate.getMonth() + 1 < 10 ? `0${tempFromDate.getMonth() + 1}` : tempFromDate.getMonth() + 1
        }-${tempFromDate.getDate() < 10 ? `0${tempFromDate.getDate()}` : tempFromDate.getDate()}`,
    );

    const dateList: string[] = [];
    const dateItem = new Date(`${fromDate} 00:00:00`);
    for (let i = 0; i < 14; i++) {
        dateList.push(
            `${dateItem.getMonth() + 1 < 10 ? `0${dateItem.getMonth() + 1}` : dateItem.getMonth() + 1}/${
                dateItem.getDate() < 10 ? `0${dateItem.getDate()}` : dateItem.getDate()
            }${[6, 0].includes(dateItem.getDay()) ? '|' : ''}`,
        );
        dateItem.setDate(dateItem.getDate() + 1);
    }

    const { loading: jobLogSummaryLoading, error: jobLogSummaryError, data: jobLogSummaryData } = useQuery<
        GetJobLogSummary
    >(GET_JOB_LOGS_SUMMARY, {
        variables: {
            input: {
                jobId: jobId,
                fromDate: `${tempFromDate.getFullYear()}-${
                    tempFromDate.getMonth() + 1 < 10 ? `0${tempFromDate.getMonth() + 1}` : tempFromDate.getMonth() + 1
                }-${tempFromDate.getDate() < 10 ? `0${tempFromDate.getDate()}` : tempFromDate.getDate()}`,
                toDate: `${today.getFullYear()}-${
                    today.getMonth() + 1 < 10 ? `0${today.getMonth() + 1}` : today.getMonth() + 1
                }-${today.getDate() < 10 ? `0${today.getDate()}` : today.getDate()}`,
                getdaywisestatsonly: false,
                tzinfo: new Date()
                    .toString()
                    .split('(')[1]
                    .replace(')', ''),
            },
        },
        fetchPolicy: 'network-only',
    });

    const { loading: jobLogsLoading, error: jobLogsError, data: jobLogsData, previousData: prevJobLogsData } = useQuery<GetJobLogSummary>(
        GET_JOB_LOGS_SUMMARY,
        {
            variables: {
                input: {
                    jobId: jobId,
                    fromDate: fromDate,
                    toDate: toDate,
                    getdaywisestatsonly: true,
                    tzinfo: new Date()
                        .toString()
                        .split('(')[1]
                        .replace(')', ''),
                },
            },
            fetchPolicy: 'network-only',
        },
    );

    const jobLogsForPieChart: LogTypesSummaryType[] = [];
    const jobLogsForGraph: Record<string, { issue: string | null; subCategory: unknown[] | null } | unknown[]> = {};
    const startEndStageGraphData: any[] = [];
    const START_END_STAGE_X_AXIS_KEY = 'dayIndex';
    let noStartEndGraphData = true;

    let jobLogs: (PivotDataType | null)[] = [];
    const jobLogsTotals = new Array(14).fill(0);
    let spillCount = 0;

    React.useEffect(()=> {
        if(jobLogSummaryData && jobLogSummaryData.getJobLogSummary){
        setJobName(jobLogSummaryData.getJobLogSummary.wellName ?? '');
        }
    },[jobLogSummaryData]);

    // if(!jobLogSummaryData && )

    if (jobLogSummaryData && jobLogSummaryData.getJobLogSummary) {
        // setJobName(jobLogSummaryData.getJobLogSummary.wellName ?? '');

        if (jobLogSummaryData.getJobLogSummary.logTypesSummary) {
            jobLogSummaryData.getJobLogSummary.logTypesSummary.map(data => {
                if (data !== null) jobLogsForPieChart.push(data);
            });
        }

        if (jobLogSummaryData.getJobLogSummary.spillsCount) {
            spillCount = jobLogSummaryData.getJobLogSummary.spillsCount;
        }

        if (toggle === 'count') {
            if (jobLogSummaryData.getJobLogSummary.logCountSummary !== null) {
                jobLogSummaryData.getJobLogSummary.logCountSummary.map(data => {
                    if (data !== null && data.issue) {
                        jobLogsForGraph[data.issue] = data;
                    }
                });
            }
        } else {
            if (jobLogSummaryData.getJobLogSummary.logTimeSummary !== null) {
                jobLogSummaryData.getJobLogSummary.logTimeSummary.map(data => {
                    if (data !== null && data.issue) {
                        jobLogsForGraph[data.issue] = data;
                    }
                });
            }
        }

        let isNPT = false;
        let isODT = false;

        Object.keys(jobLogsForGraph).map(logs => {
            if (logs === 'SandBox NPT') isNPT = true;
            if (logs === 'Onsite Downtime') isODT = true;
        });

        if (!isNPT) jobLogsForGraph['SandBox NPT'] = [];
        if (!isODT) jobLogsForGraph['Onsite Downtime'] = [];

        if (jobLogSummaryData.getJobLogSummary.stageStEndSummary !== null) {
            const wellheadCount = jobLogSummaryData.getJobLogSummary.stageStEndSummary.length;
            const temp = {};

            for (let i = 0; i < wellheadCount; i++) {
                if (
                    jobLogSummaryData.getJobLogSummary.stageStEndSummary[i]?.wellHead &&
                    ((jobLogSummaryData.getJobLogSummary.stageStEndSummary[i]?.statsType === 'Count' &&
                        toggle === 'count') ||
                        (jobLogSummaryData.getJobLogSummary.stageStEndSummary[i]?.statsType === 'Time' &&
                            toggle === 'time'))
                )
                    temp[`${jobLogSummaryData.getJobLogSummary.stageStEndSummary[i]?.wellHead}`] = 0;
            }
            for (let i = 0; i < 14; i++) {
                const rowData = JSON.parse(JSON.stringify(temp));
                rowData[START_END_STAGE_X_AXIS_KEY] = i;
                startEndStageGraphData.push(rowData);
            }

            jobLogSummaryData.getJobLogSummary.stageStEndSummary.map(data => {
                if (
                    data !== null &&
                    ((data.statsType === 'Count' && toggle === 'count') ||
                        (data.statsType === 'Time' && toggle === 'time'))
                ) {
                    startEndStageGraphData.map((tempData, index) => {
                        if (data.wellHead) {
                            noStartEndGraphData = false;
                            tempData[data.wellHead] = data[`statsonDate${index + 1}`];
                        }
                    });
                }
            });
        }
    }

    if(!jobLogsData && prevJobLogsData){
        if (prevJobLogsData.getJobLogSummary !==null && prevJobLogsData.getJobLogSummary.pivotData !== null) {
            jobLogs = prevJobLogsData!.getJobLogSummary.pivotData;
            jobLogs.map(jobLog => {
                jobLogsTotals[0] += jobLog ? jobLog.countonDate1 : 0;
                jobLogsTotals[1] += jobLog ? jobLog.countonDate2 : 0;
                jobLogsTotals[2] += jobLog ? jobLog.countonDate3 : 0;
                jobLogsTotals[3] += jobLog ? jobLog.countonDate4 : 0;
                jobLogsTotals[4] += jobLog ? jobLog.countonDate5 : 0;
                jobLogsTotals[5] += jobLog ? jobLog.countonDate6 : 0;
                jobLogsTotals[6] += jobLog ? jobLog.countonDate7 : 0;
                jobLogsTotals[7] += jobLog ? jobLog.countonDate8 : 0;
                jobLogsTotals[8] += jobLog ? jobLog.countonDate9 : 0;
                jobLogsTotals[9] += jobLog ? jobLog.countonDate10 : 0;
                jobLogsTotals[10] += jobLog ? jobLog.countonDate11 : 0;
                jobLogsTotals[11] += jobLog ? jobLog.countonDate12 : 0;
                jobLogsTotals[12] += jobLog ? jobLog.countonDate13 : 0;
                jobLogsTotals[13] += jobLog ? jobLog.countonDate14 : 0;
            });
        }
    }

    if (jobLogsData && jobLogsData.getJobLogSummary) {
        if (jobLogsData.getJobLogSummary.pivotData !== null) {
            jobLogs = jobLogsData.getJobLogSummary.pivotData;
        }

        jobLogs.map(jobLog => {
            jobLogsTotals[0] += jobLog ? jobLog.countonDate1 : 0;
            jobLogsTotals[1] += jobLog ? jobLog.countonDate2 : 0;
            jobLogsTotals[2] += jobLog ? jobLog.countonDate3 : 0;
            jobLogsTotals[3] += jobLog ? jobLog.countonDate4 : 0;
            jobLogsTotals[4] += jobLog ? jobLog.countonDate5 : 0;
            jobLogsTotals[5] += jobLog ? jobLog.countonDate6 : 0;
            jobLogsTotals[6] += jobLog ? jobLog.countonDate7 : 0;
            jobLogsTotals[7] += jobLog ? jobLog.countonDate8 : 0;
            jobLogsTotals[8] += jobLog ? jobLog.countonDate9 : 0;
            jobLogsTotals[9] += jobLog ? jobLog.countonDate10 : 0;
            jobLogsTotals[10] += jobLog ? jobLog.countonDate11 : 0;
            jobLogsTotals[11] += jobLog ? jobLog.countonDate12 : 0;
            jobLogsTotals[12] += jobLog ? jobLog.countonDate13 : 0;
            jobLogsTotals[13] += jobLog ? jobLog.countonDate14 : 0;
        });
    }

    const onDateChange = value => {
        const newFromDate = new Date(value);
        const newToDate = new Date(value);
        if (window.screen.width > 1199) newToDate.setDate(newFromDate.getDate() + 13);
        else newToDate.setDate(newFromDate.getDate() + 6);

        document.getElementById('JobLogs-DateFilter-Container')?.click();
        setFromDate(
            `${newFromDate.getFullYear()}-${
                newFromDate.getMonth() + 1 < 10 ? `0${newFromDate.getMonth() + 1}` : newFromDate.getMonth() + 1
            }-${newFromDate.getDate() < 10 ? `0${newFromDate.getDate()}` : newFromDate.getDate()}`,
        );
        setToDate(
            `${newToDate.getFullYear()}-${
                newToDate.getMonth() + 1 < 10 ? `0${newToDate.getMonth() + 1}` : newToDate.getMonth() + 1
            }-${newToDate.getDate() < 10 ? `0${newToDate.getDate()}` : newToDate.getDate()}`,
        );
    };

    const onClickPrev = () => {
        const firstDay = new Date(`${fromDate} 00:00:00`);
        const newToDate = new Date(firstDay);
        const newFromDate = new Date(firstDay);

        newToDate.setDate(firstDay.getDate() - 1);

        if (window.screen.width > 1199) newFromDate.setDate(newFromDate.getDate() - 14);
        else newFromDate.setDate(newFromDate.getDate() - 7);

        setFromDate(
            `${newFromDate.getFullYear()}-${
                newFromDate.getMonth() + 1 < 10 ? `0${newFromDate.getMonth() + 1}` : newFromDate.getMonth() + 1
            }-${newFromDate.getDate() < 10 ? `0${newFromDate.getDate()}` : newFromDate.getDate()}`,
        );
        setToDate(
            `${newToDate.getFullYear()}-${
                newToDate.getMonth() + 1 < 10 ? `0${newToDate.getMonth() + 1}` : newToDate.getMonth() + 1
            }-${newToDate.getDate() < 10 ? `0${newToDate.getDate()}` : newToDate.getDate()}`,
        );
    };

    const onClickNext = () => {
        const nextDay = new Date(`${toDate} 00:00:00`);
        const newToDate = new Date(nextDay);
        const newFromDate = new Date(nextDay);

        newFromDate.setDate(nextDay.getDate() + 1);

        if (window.screen.width > 1199) newToDate.setDate(newToDate.getDate() + 14);
        else newToDate.setDate(newToDate.getDate() + 7);

        setFromDate(
            `${newFromDate.getFullYear()}-${
                newFromDate.getMonth() + 1 < 10 ? `0${newFromDate.getMonth() + 1}` : newFromDate.getMonth() + 1
            }-${newFromDate.getDate() < 10 ? `0${newFromDate.getDate()}` : newFromDate.getDate()}`,
        );
        setToDate(
            `${newToDate.getFullYear()}-${
                newToDate.getMonth() + 1 < 10 ? `0${newToDate.getMonth() + 1}` : newToDate.getMonth() + 1
            }-${newToDate.getDate() < 10 ? `0${newToDate.getDate()}` : newToDate.getDate()}`,
        );
    };

    const CustomPieLabel = (props: {
        cx?: number;
        cy?: number;
        midAngle?: number;
        innerRadius?: number;
        outerRadius?: number;
        percent?: number;
        index?: number;
        payload?: { payload?: { category?: string } };
    }) => {
        const { cx = 0, cy = 0, midAngle = 0, innerRadius = 0, outerRadius = 0, percent = 0, payload } = props;
        const RADIAN = Math.PI / 180;
        const xRadius = innerRadius + (outerRadius - innerRadius) * 0.05;
        const yRadius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + xRadius * Math.cos(-midAngle * RADIAN);
        const y = cy + yRadius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="black"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                fontSize={12}
                style={{ wordBreak: 'break-all' }}
            >
                {`${payload?.payload?.category ?? ''} (${(percent * 100).toFixed(0)}%)`}
            </text>
        );
    };

    const CustomPieTooltip = ({ active, payload, label, index }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className="JobLogs-Graph-Tooltip-Container"
                    style={{ backgroundColor: payload[0].fill ?? payload[0].payload.fill }}
                >
                    <p className="JobLogs-Graph-Tooltip-Data">
                        {payload[0].payload.descrip ?? payload[0].payload.category}
                    </p>
                    {toggle === 'count' ? (
                        <p className="JobLogs-Graph-Tooltip-Data">Count: {payload[0].value}</p>
                    ) : (
                        <p className="JobLogs-Graph-Tooltip-Data">Hours: {payload[0].value}</p>
                    )}
                </div>
            );
        }

        return null;
    };

    const CustomTooltip = ({ active, payload, label, index }: any) => {
        if (active && payload && payload.length) {
            return (
                <div
                    className="JobLogs-Graph-Tooltip-Container"
                    style={{ backgroundColor: payload[0].fill ?? payload[0].payload.fill }}
                >
                    <p className="JobLogs-Graph-Tooltip-Data">
                        {payload[0].payload.descrip ?? payload[0].payload.category}
                    </p>
                    <p className="JobLogs-Graph-Tooltip-Data">
                        {payload[0].dataKey} : {payload[0].value} {toggle === 'time' ? 'Hours' : ''}
                    </p>
                </div>
            );
        }

        return null;
    };

    const CustomizedAxisTick = ({ x, y, payload }: any) => {
        let chippedLabel = payload.value.substring(0, 15);
        if (chippedLabel !== payload.value) chippedLabel += '..';
        return (
            <Text x={x} y={y} width={55} textAnchor="middle" verticalAnchor="start">
                {chippedLabel}
            </Text>
        );
    };

    const CustomTooltipForStage = ({ active, payload, label, index }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="JobLogs-Graph-Tooltip-Container-For-Stage" style={{ backgroundColor: '#fff' }}>
                    {payload.map((load, index) => (
                        <p key={index} className="JobLogs-Graph-Tooltip-Data" style={{ color: load.color }}>
                            {load.dataKey} : {load.value} {toggle === 'time' ? 'Hours' : ''}
                        </p>
                    ))}
                </div>
            );
        }

        return null;
    };

    const CustomizedAxisTickForStageX = ({ x, y, payload }: any) => {
        const date = new Date(tempFromDate);
        date.setDate(date.getDate() + payload.index);

        return (
            <Text x={x} y={y} width={55} textAnchor="middle" verticalAnchor="start" fontSize={12}>
                {`${date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1}-${
                    date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()
                }`}
            </Text>
        );
    };

    const CustomizedAxisTickForStageY = ({ x, y, payload }: any) => {
        return (
            <Text x={x - 10} y={y - 4} width={55} textAnchor="middle" verticalAnchor="start" fontSize={12}>
                {payload.value < 0 ? '' : payload.value}
            </Text>
        );
    };

    if (jobLogSummaryLoading) {
        return (
            <div className="JobLogs-Loading">
                <Loading />
            </div>
        );
    }

    if (jobLogSummaryError) {
        return <Error error={jobLogSummaryError} />;
    }

    if (jobLogsError) {
        return <Error error={jobLogsError} />;
    }

    return (
        <div className="JobLogs-Summary">
            {jobLogsForPieChart.length === 0 && Object.keys(jobLogsForGraph).length === 0 ? (
                <Col className="JobLogs-Graphs-Container">
                    <div className="JobLogs-Summary-NoResult-Container">
                        <div className="NoResult-Second-Title-Primary">No Job Log Summary Available</div>
                    </div>
                </Col>
            ) : (
                <Col className="JobLogs-Graphs-Container">
                    <div className={`JobLogs-Graph JobLogs-Graph-xLg`}>
                        <div className="JobLogs-Toggle-Box-Container-For-Stage">
                            <div className="JobLogs-Graph-Title">Start/ End Stage</div>
                        </div>
                        {noStartEndGraphData ? (
                            <div className="JobLogs-Graph-NoLogs">No Logs Found</div>
                        ) : (
                            <ResponsiveContainer width="100%" height="99%">
                                <LineChart
                                    data={startEndStageGraphData}
                                    margin={{
                                        right: 30,
                                    }}
                                    stackOffset="sign"
                                >
                                    <XAxis dataKey={START_END_STAGE_X_AXIS_KEY} tick={<CustomizedAxisTickForStageX />} />
                                    <YAxis tick={<CustomizedAxisTickForStageY />} allowDecimals={false}>
                                        {toggle === 'count' ? (
                                            <Label position="insideLeft" value="No. of Stages" angle={-90} />
                                        ) : (
                                            <Label position="insideLeft" value="Hours" angle={-90} />
                                        )}
                                    </YAxis>
                                    <Tooltip content={<CustomTooltipForStage />} />
                                    <Legend wrapperStyle={{ top: -20, right: 25, whiteSpace: 'break-spaces' }} />
                                    {(startEndStageGraphData[0]
                                        ? Object.keys(startEndStageGraphData[0]).filter(
                                              key => key !== START_END_STAGE_X_AXIS_KEY,
                                          )
                                        : []
                                    ).map((wellheads, index) => (
                                        <Line
                                            key={index}
                                            type="monotone"
                                            dataKey={wellheads}
                                            stroke={LegendColors[index]}
                                            activeDot={{ r: 8 }}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {jobLogsForPieChart.length > 0 ? (
                        <div className="JobLogs-Graph JobLogs-Graph-Pie">
                            <div className="JobLogs-Graph-Title">
                                Log Types Summary
                                <div className="JobLogs-Spills-Count">
                                    {spillCount}
                                    <span className="JobLogs-Spills-Count-Sub">No. of Spills</span>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        dataKey={toggle === 'count' ? 'totalCount' : 'totalTime'}
                                        isAnimationActive={true}
                                        animationBegin={0}
                                        animationDuration={800}
                                        data={jobLogsForPieChart as any}
                                        fill="#8884d8"
                                        label={CustomPieLabel as any}
                                        labelLine={false}
                                    >
                                        {jobLogsForPieChart.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={LegendColorsForPie[index]}
                                                style={{
                                                    filter: `drop-shadow(4px 2px 10px black)`,
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip shared={false} content={<CustomPieTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="JobLogs-Graph">
                            <div className="JobLogs-Graph-Title">
                                Log Types Summary
                                <div className="JobLogs-Spills-Count">
                                    {spillCount}
                                    <span className="JobLogs-Spills-Count-Sub">No. of Spills</span>
                                </div>
                            </div>
                            <div className="JobLogs-Graph-NoLogs">No Logs Found</div>
                        </div>
                    )}

                    {Object.keys(jobLogsForGraph).map((jobLogForGraph, index) => {
                        const graphVal = jobLogsForGraph[jobLogForGraph];
                        if (Array.isArray(graphVal) && graphVal.length === 0) {
                            return (
                                <div className="JobLogs-Graph" key={`joblog${index}`}>
                                    <div className="JobLogs-Graph-Title">{jobLogForGraph}</div>
                                    <div className="JobLogs-Graph-NoLogs">No Logs Found</div>
                                </div>
                            );
                        } else {
                            const graphEntry = graphVal as { issue: string | null; subCategory: unknown[] | null };
                            return (
                                graphEntry.subCategory != null && (
                                    <div className="JobLogs-Graph" key={index}>
                                        <div className="JobLogs-Graph-Title">
                                            {graphEntry.issue}
                                        </div>
                                        {/* <ResponsiveContainer width={jobLogForGraph.subCategory.length <= 2 ? "50%" : "100%"} height={300}> */}
                                        <ResponsiveContainer width="100%" height="99%">
                                            <BarChart data={graphEntry.subCategory} barSize={30}>
                                                <XAxis
                                                    dataKey="descrip"
                                                    type="category"
                                                    tick={<CustomizedAxisTick />}
                                                    interval={0}
                                                />
                                                <YAxis
                                                    allowDecimals={false}
                                                    domain={[0, (dataMax: number) => Math.ceil(dataMax)]}
                                                >
                                                    {toggle === 'count' ? (
                                                        <Label
                                                            position="insideLeft"
                                                            value="Count of Job Log Type"
                                                            angle={-90}
                                                        />
                                                    ) : (
                                                        <Label position="insideLeft" value="Hours" angle={-90} />
                                                    )}
                                                </YAxis>
                                                <Bar
                                                    dataKey="total"
                                                    fill={LegendColors[index]}
                                                    isAnimationActive={true}
                                                >
                                                    {graphEntry.subCategory.map((entry, idx) => (
                                                        <Cell
                                                            key={`cell-${idx}`}
                                                            style={{
                                                                filter: `drop-shadow(3px 0px 3px black)`,
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    ))}
                                                </Bar>
                                                <Tooltip shared={false} content={<CustomTooltip />} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )
                            );
                        }
                    })}
                </Col>
            )}

            <div className="JobLogs-Filter-Container">
                <Dropdown>
                    <Dropdown.Toggle variant="outline-dark" id="JobLogs-DateFilter-Container">
                        <span className="JobLogs-DateFilter-Text">
                            {fromDate} To {toDate}
                        </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="JobLogs-DateFilter-Dropdown-Container">
                        <div className="JobLogs-DateFilter-Dropdown">
                            <DateTimePicker
                                dateOnly
                                label="Choose FROM date"
                                error={''}
                                open={true}
                                onChange={(value: any) => onDateChange(value)}
                            />
                        </div>
                    </Dropdown.Menu>
                </Dropdown>

                <div className="JobLogs-PrevNext-Container">
                    <div className="JobLogs-Prev-Button">
                        <span className="JobLogs-Button-Text" onClick={() => onClickPrev()}>
                            <i className="material-icons">chevron_left</i>
                            Previous
                        </span>
                    </div>

                    <div className="JobLogs-Prev-Button">
                        <span className="JobLogs-Button-Text" onClick={() => onClickNext()}>
                            Next
                            <i className="material-icons">chevron_right</i>
                        </span>
                    </div>
                </div>
            </div>

            <Col>
                <Row>
                    {window.screen.width > 1199 ? (
                        <Table bordered className="JobLogs-Table">
                            {jobLogsLoading && (
                                <div className="JobLogs-Loading">
                                    <Loading />
                                </div>
                            )}
                            <tr className="JobLogs-Table-Dates">
                                <th></th>
                                {dateList.map((date, index) => (
                                    <th key={index} className={date.includes('|') ? 'JobLogs-Weekend' : ''}>
                                        {date.replace('|', '')}
                                    </th>
                                ))}
                            </tr>

                            <tr>
                                <th className="JobLogs-Header JobLogs-ClmLg">Job Log Type</th>
                            </tr>

                            {jobLogs.map((jobLog, index) => (
                                <tr key={index}>
                                    <td>{jobLog ? jobLog.jobLogType : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate1 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate2 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate3 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate4 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate5 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate6 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate7 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate8 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate9 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate10 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate11 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate12 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate13 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate14 : '-'}</td>
                                </tr>
                            ))}

                            <tr className="JobLogs-Total-Row">
                                <th className="JobLogs-Total-Header">Total</th>
                                {jobLogsTotals.map((total, index) => (
                                    <th key={index}>{total}</th>
                                ))}
                            </tr>
                            <td colSpan={16}>&nbsp;</td>
                        </Table>
                    ) : (
                        <Table bordered className="JobLogs-Table">
                            {jobLogsLoading && (
                                <div className="JobLogs-Loading">
                                    <Loading />
                                </div>
                            )}
                            <tr className="JobLogs-Table-Dates">
                                <th></th>
                                {dateList.slice(0, 7).map((date, index) => (
                                    <th key={index} className={date.includes('|') ? 'JobLogs-Weekend' : ''}>
                                        {date.replace('|', '')}
                                    </th>
                                ))}
                            </tr>

                            <tr>
                                <th className="JobLogs-Header JobLogs-ClmLg">Job Log Type</th>
                            </tr>

                            {jobLogs.map((jobLog, index) => (
                                <tr key={index}>
                                    <td>{jobLog ? jobLog.jobLogType : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate1 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate2 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate3 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate4 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate5 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate6 : '-'}</td>
                                    <td>{jobLog ? jobLog.countonDate7 : '-'}</td>
                                </tr>
                            ))}

                            <tr className="JobLogs-Total-Row">
                                <th className="JobLogs-Total-Header">Total</th>
                                {jobLogsTotals.slice(0, 7).map((total, index) => (
                                    <th key={index}>{total}</th>
                                ))}
                            </tr>
                            <td colSpan={16}>&nbsp;</td>
                        </Table>
                    )}
                </Row>
            </Col>
        </div>
    );
};

export default JobLogSummary;
